import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	VictoryAxis,
	VictoryChart,
	VictoryLegend,
	VictoryLine,
	VictoryScatter,
} from 'victory';
import dayjs from 'dayjs';
import { ConsumeItemTypeDashboardDto, ConsumesDashboardDto } from 'api/dashboard/dtos/ConsumesDashboardDto';
import styles from './ConsumesChart.module.scss';
import Utils from 'common/services/Utils';
import { DASHBOARD_COLORS, DEFAULT_LANGUAGE, STORAGE } from 'Config';
import { DashboardConsumeTypeEnum } from 'api/dashboard/enums/DashboardConsumeTypeEnum';
import ChartLegendLabel from 'common/components/charts/ChartLegendLabel';
import Colors from 'styles/export/colors.module.scss';
import ChartEmptyLabel from 'common/components/charts/ChartEmptyLabel';
import useElementSize from 'common/hooks/useElementSize';
import Storage from 'common/services/Storage';
import { DashboardFuelTypeEnum } from 'api/dashboard/enums/DashboardFuelTypeEnum';
import { GenericType } from 'api/genericTypes/enums/GenericType';
import GenericTypesService from 'api/genericTypes/GenericTypesService';
import ChartTooltipPortal from '../components/ChartTooltipPortal';
import ChartAveragePill from '../components/ChartAveragePill';
import WidgetNoData from '../components/WidgetNoData';

type Props = {
	data: ConsumesDashboardDto;
	selectedYear: number | null;
};

export interface ConsumesDashboardHiddenDataDto {
	type: DashboardConsumeTypeEnum;
	fuelName: DashboardFuelTypeEnum;
}

const ConsumesChart = ({ data, selectedYear }: Props) => {
	const { t } = useTranslation();
	const [hiddenData, setHiddenData] = useState<ConsumesDashboardHiddenDataDto[]>([]);
	const [reRenderKey, setReRenderKey] = useState(new Date().getTime());
	const [containerRef, { width, height }] = useElementSize();
	const [tooltip, setTooltip] = useState<{ dateTime: number; clientX: number; clientY: number } | null>(null);
	const [avgTooltip, setAvgTooltip] = useState<{ clientX: number; clientY: number } | null>(null);
	const locale = Storage.get(STORAGE.CURRENT_LOCALE) || DEFAULT_LANGUAGE;
	const fuelTypeIdRef = useRef<string | null>(null);

	useEffect(() => {
		setReRenderKey(new Date().getTime());
	}, [data, selectedYear, width, height]);

	useEffect(() => {
		let cancelled = false;
		GenericTypesService.catalogCharge()
			.then(types => {
				if (cancelled) return;
				// Backend maps `gt.name` to the DTO `label` column, so that's the field holding 'FUEL'.
				const fuel = (types || []).find(x => x.label === GenericType.FUEL || x.name === GenericType.FUEL);
				fuelTypeIdRef.current = fuel?.value ?? null;
			})
			.catch(() => { /* ignore */ });
		return () => { cancelled = true; };
	}, []);

	if (!data.items || data.items.length === 0) {
		return <WidgetNoData />;
	}

	const chartWidth = Math.max(width, 100);
	const legendHeight = 28;
	const chartHeight = Math.max(height, 60);
	const chartPadding = { top: 15, left: 65, right: 25, bottom: 35 + legendHeight };

	let itemsData = data.items.filter(x => !hiddenData.find(h => h.type === x.type && h.fuelName === x.fuelName));

	if (selectedYear) {
		itemsData = itemsData.map(x => ({
			...x,
			items: x.items.filter(s => dayjs(s.date).year() === selectedYear),
		}));
	}

	const allItems = Utils.flatten<ConsumeItemTypeDashboardDto>(itemsData.map(x => x.items));
	const allDates = allItems
		.map(x => x.date)
		.filter((date, i, self) => self.findIndex(d => d.getTime() === date.getTime()) === i);

	// For line chart, compute max value per individual series (not stacked)
	const allValues = allItems.map(x => x.value);
	const highestValue = allValues.length > 0 ? Math.max(...allValues) : 0;
	const average = allValues.length > 0 ? allValues.reduce((a, b) => a + b, 0) / allValues.length : 0;

	// Y pixel of the dashed average line, used to position the transparent hit-zone.
	const plotTop = chartPadding.top;
	const plotBottom = chartHeight - chartPadding.bottom;
	const plotHeight = Math.max(plotBottom - plotTop, 0);
	const avgYPx = highestValue > 0
		? plotTop + plotHeight * (1 - average / highestValue)
		: plotBottom;
	const AVG_HIT_HALF_HEIGHT = 6;

	// Build month breakdown lookup keyed by YYYY-MM to avoid timestamp mismatches between series
	const toMonthKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}`;
	let allItemsForTooltip = data.items;
	if (selectedYear) {
		allItemsForTooltip = allItemsForTooltip.map(x => ({
			...x,
			items: x.items.filter(s => dayjs(s.date).year() === selectedYear),
		}));
	}
	const monthBreakdown = new Map<string, { fuels: { fuelName: DashboardFuelTypeEnum; value: number }[]; total: number }>();
	allDates.forEach(date => {
		const key = toMonthKey(date);
		if (monthBreakdown.has(key)) return;
		const fuels = allItemsForTooltip.map(x => ({
			fuelName: x.fuelName,
			value: x.items.find(i => toMonthKey(i.date) === key)?.value ?? 0,
		}));
		const total = fuels.reduce((sum, f) => sum + f.value, 0);
		monthBreakdown.set(key, { fuels, total });
	});

	const legendData = [
		...data.items.map(x => ({
			name: t(`vehicle.fuels.${x.fuelName}` as any),
			fuelName: x.fuelName,
			type: x.type,
			symbol: { fill: !hiddenData.find(a => a.type === x.type && a.fuelName === x.fuelName) ? DASHBOARD_COLORS.fuels[x.fuelName] : Colors.gray17 },
			labels: { fill: Colors.primaryDark },
			clickable: true,
		})),
	];

	const onClickLegend = (propsData: any) => {
		const type = propsData.datum.type;
		const fuelName = propsData.datum.fuelName;
		if (!type) return;

		if (hiddenData.find(x => x.type === type && x.fuelName === fuelName)) {
			setHiddenData(hiddenData.filter(x => !(x.type === type && x.fuelName === fuelName)));
		} else {
			setHiddenData([...hiddenData, { type, fuelName }]);
		}
		setReRenderKey(new Date().getTime());
	};

	// Estimate legend width for centering
	const legendItemWidth = 130;
	const legendTotalWidth = legendData.length * legendItemWidth;
	const legendX = Math.max((chartWidth - legendTotalWidth) / 2, 0);

	const tooltipData = tooltip ? monthBreakdown.get(toMonthKey(new Date(tooltip.dateTime))) : null;

	const resolveFuelTypeId = async (): Promise<string | null> => {
		if (fuelTypeIdRef.current) return fuelTypeIdRef.current;
		try {
			const types = await GenericTypesService.catalogCharge();
			const fuel = (types || []).find(x => x.label === GenericType.FUEL || x.name === GenericType.FUEL);
			fuelTypeIdRef.current = fuel?.value ?? null;
		} catch {
			// ignore
		}
		return fuelTypeIdRef.current;
	};

	const goToCharges = async (date: Date) => {
		const startDate = new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0);
		const endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59);
		const typeId = await resolveFuelTypeId();
		try {
			// Replace filters entirely. The /charges page defaults vehiclesInactive=true,
			// so we must explicitly set it to false to match what the widget shows (active only).
			const next: Record<string, unknown> = {
				startDate,
				endDate,
				vehiclesInactive: false,
			};
			if (typeId) {
				next.typeId = typeId;
				next.type = GenericType.FUEL;
				next.typeName = t('common.generic_types.FUEL' as any);
			}
			localStorage.setItem('CHARGESFILTER-0', JSON.stringify(next));
		} catch {
			// ignore
		}
		window.open('/charges', '_blank');
	};

	const scatterEvents: any[] = [
		{
			target: 'data',
			eventHandlers: {
				onMouseEnter: (evt: any, targetProps: any) => {
					setTooltip({
						dateTime: targetProps.datum.date.getTime(),
						clientX: evt.clientX,
						clientY: evt.clientY,
					});
					return [];
				},
				onMouseMove: (evt: any) => {
					setTooltip(prev => prev ? { ...prev, clientX: evt.clientX, clientY: evt.clientY } : null);
					return [];
				},
				onMouseLeave: () => {
					setTooltip(null);
					return [];
				},
				onClick: (_evt: any, targetProps: any) => {
					goToCharges(targetProps.datum.date);
					return [];
				},
			},
		},
	];

	return (
		<div className={styles.chartContainer} ref={containerRef}>
			{width > 0 && height > 0 && (
				<svg width={width} height={height} key={'svg-' + reRenderKey}>
					{/* Average Line */}
					{itemsData.length > 0 && (
						<VictoryChart
							standalone={false}
							width={chartWidth}
							height={chartHeight}
							padding={chartPadding}
							maxDomain={{ y: highestValue }}
							minDomain={{ y: 0 }}
						>
							<VictoryLine
								style={{
									data: {
										stroke: Colors.primaryDark,
										strokeWidth: 1,
										strokeDasharray: 4,
									},
								}}
								data={[
									{ x: 0, y: average },
									{ x: 1, y: average },
								]}
							/>
							<VictoryAxis
								dependentAxis
								standalone={false}
								tickValues={allValues}
								style={{
									axis: { stroke: 'transparent', fill: 'none' },
									ticks: { stroke: 'transparent', fill: 'none' },
									axisLabel: { stroke: 'transparent', fill: 'none' },
								}}
								tickLabelComponent={<ChartEmptyLabel />}
							/>
						</VictoryChart>
					)}

					{/* Main line chart */}
					{itemsData.length > 0 && (
						<VictoryChart
							standalone={false}
							width={chartWidth}
							height={chartHeight}
							padding={chartPadding}
							scale={{ x: 'time' }}
							domainPadding={{ x: 30 }}
							key={'chart-' + reRenderKey}
						>
							{itemsData.map((item, i) => (
								<VictoryLine
									key={'line-' + item.type + '-' + i}
									data={item.items}
									x="date"
									y="value"
									interpolation="monotoneX"
									style={{
										data: {
											stroke: DASHBOARD_COLORS.fuels[item.fuelName],
											strokeWidth: 2,
										},
									}}
								/>
							))}
							{itemsData.map((item, i) => (
								<VictoryScatter
									key={'scatter-' + item.type + '-' + i}
									data={item.items.filter(d => d.value > 0)}
									x="date"
									y="value"
									size={4}
									style={{
										data: {
											fill: DASHBOARD_COLORS.fuels[item.fuelName],
											cursor: 'pointer',
										},
									}}
									events={scatterEvents}
								/>
							))}
							<VictoryAxis
								dependentAxis
								style={{
									axis: { stroke: 'transparent' },
									grid: { stroke: '#f5f5f5' },
									tickLabels: { fontSize: 10, fontFamily: 'Roboto', fill: '#6b7280' },
								}}
								tickFormat={x => Utils.formatCurrency(x)}
							/>
							<VictoryAxis
								orientation="bottom"
								width={chartWidth}
								height={chartHeight}
								tickValues={allDates}
								tickFormat={x => Utils.capitalize(dayjs(x).locale(locale).format('MMM')) + ' ' + dayjs(x).format('YY')}
								style={{
									tickLabels: { fill: Colors.black, userSelect: 'none', fontSize: 12, fontFamily: 'Roboto' },
									axis: { stroke: Colors.gray3 },
								}}
							/>
						</VictoryChart>
					)}

					{/* Legends */}
					<VictoryLegend
						standalone={false}
						x={legendX}
						y={chartHeight - legendHeight}
						gutter={40}
						data={legendData}
						orientation={'horizontal'}
						itemsPerRow={3}
						labelComponent={<ChartLegendLabel onClick={onClickLegend} />}
						style={{ labels: { fontSize: 12, userSelect: 'none', fontFamily: 'Roboto' } }}
					/>
					{/* Thin hit-zone over the dashed average line — rendered last so it captures
					    hover events within its band and shows the average value tooltip. */}
					{itemsData.length > 0 && highestValue > 0 && (
						<rect
							x={chartPadding.left}
							y={avgYPx - AVG_HIT_HALF_HEIGHT}
							width={chartWidth - chartPadding.left - chartPadding.right}
							height={AVG_HIT_HALF_HEIGHT * 2}
							fill="transparent"
							style={{ cursor: 'pointer' }}
							onMouseMove={evt => {
								setAvgTooltip({ clientX: evt.clientX, clientY: evt.clientY });
							}}
							onMouseLeave={() => setAvgTooltip(null)}
						/>
					)}
				</svg>
			)}

			{tooltip && tooltipData && (
				<ChartTooltipPortal clientX={tooltip.clientX} clientY={tooltip.clientY} className={styles.tooltip}>
					{tooltipData.fuels.map(fuel => (
						<div key={fuel.fuelName} className={styles.tooltipRow}>
							<span className={styles.tooltipDot} style={{ backgroundColor: DASHBOARD_COLORS.fuels[fuel.fuelName] }} />
							<span>{t(`vehicle.fuels.${fuel.fuelName}` as any)}: {Utils.formatCurrency(fuel.value)}</span>
						</div>
					))}
					<div className={`${styles.tooltipRow} ${styles.tooltipTotal}`}>
						<span>Total: {Utils.formatCurrency(tooltipData.total)}</span>
					</div>
				</ChartTooltipPortal>
			)}

			{avgTooltip && !tooltip && (
				<ChartAveragePill clientX={avgTooltip.clientX} clientY={avgTooltip.clientY} color={Colors.primaryDark}>
					{t('dashboard.routes.AVERAGE_OVER' as any, { value: Utils.formatCurrency(average) })}
				</ChartAveragePill>
			)}
		</div>
	);
};

export default ConsumesChart;
